import React, { useState, useEffect } from 'react';
import { getStaffs } from '../../../services/api';
import LoadingSpinner from '../../common/LoadingSpinner';
import AlertMessage from '../../common/AlertMessage';
import '../BookingForm.css';

const BarberSelection = ({ selectedBarber, onBarberSelect, corporationId }) => {
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBarbers = async () => {
      if (!corporationId) {
        setError('Corporation ID is required. Please check your .env file and restart the development server.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getStaffs(corporationId);
        
        if (response.success && response.data?.staffs) {
          // Filter out "any" staff if it exists in the API response to avoid duplicates
          const filteredBarbers = response.data.staffs.filter(barber => barber.id !== 'any');
          setBarbers(filteredBarbers);
        } else {
          setError('Failed to load professionals');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Error loading professionals. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchBarbers();
  }, [corporationId]);

  const handleBarberChange = (barber) => {
    if (onBarberSelect) {
      onBarberSelect(barber);
    }
  };

  if (loading) {
    return <LoadingSpinner className="my-4" />;
  }

  if (error) {
    return <AlertMessage variant="danger" message={error} />;
  }

  if (barbers.length === 0) {
    return (
      <AlertMessage 
        variant="info" 
        message="No professionals available at the moment." 
      />
    );
  }

  const handleAnyStaffSelect = () => {
    if (onBarberSelect) {
      onBarberSelect({ id: 'any', name: 'Any Available Staff' });
    }
  };

  const isAnySelected = selectedBarber && selectedBarber.id === 'any';

  return (
    <div className="barber-selection">
      <h6 className="mb-3">Select Your Professional</h6>
      <ul className="barber-list">
        <li
          className={`barber-list-item ${isAnySelected ? 'selected' : ''}`}
          onClick={handleAnyStaffSelect}
        >
          <input
            type="radio"
            className="barber-radio"
            name="barber"
            checked={isAnySelected}
            onChange={handleAnyStaffSelect}
            id="barber-any"
          />
          <div className="barber-info">
            <label 
              htmlFor="barber-any"
              className="barber-name"
              style={{ cursor: 'pointer', margin: 0 }}
            >
              Any Available Staff
            </label>
            <p className="barber-position">We'll assign the best available professional</p>
          </div>
        </li>
        {barbers.map((barber) => {
          const isSelected = selectedBarber && selectedBarber.id === barber.id;
          
          return (
            <li
              key={barber.id}
              className={`barber-list-item ${isSelected ? 'selected' : ''}`}
              onClick={() => handleBarberChange(barber)}
            >
              <input
                type="radio"
                className="barber-radio"
                name="barber"
                checked={isSelected}
                onChange={() => handleBarberChange(barber)}
                id={`barber-${barber.id}`}
              />
              <div className="barber-info">
                <label 
                  htmlFor={`barber-${barber.id}`}
                  className="barber-name"
                  style={{ cursor: 'pointer', margin: 0 }}
                >
                  {barber.name}
                </label>
                {barber.position && (
                  <p className="barber-position">{barber.position}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default BarberSelection;
